import React, {
  useEffect,
  forwardRef,
  useMemo,
  useState,
  Fragment,
} from "react";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
// import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { parsePhoneNumber } from "libphonenumber-js";
import { postAPI } from "../api/callAPI";
import { useSnackbar } from "../Common/useSnackbar";
import PhoneNumberInput, {
  isValidPhoneNumber,
} from "../Common/PhoneNumberInput";
import { isMobileOtpEnabled } from "../Common/utils";

const AddressForm = forwardRef((Props, ref) => {
  const { handleData, onPhoneVerified, storeConfig } = Props;

  const { endpoint } = useSelector((state) => state.auth);
  const { showSnackbar } = useSnackbar();

  const otpEnabled = isMobileOtpEnabled(storeConfig?.otp_enable);

  const { termsURL, privacyURL } = useMemo(() => {
    return {
      termsURL: `https://${endpoint}.feedthetummy.com/page/terms`,
      privacyURL: `https://${endpoint}.feedthetummy.com/page/privacy-policy`,
    };
  }, [endpoint]);

  const validationSchema = useMemo(() => {
    const shape = {
      fname: Yup.string().required("Firstname is required"),
      lname: Yup.string().optional(),
      isAgree: Yup.bool().oneOf(
        [true],
        "You must agree to terms & conditions and privacy policy in order to proceed"
      ),
    };

    if (otpEnabled) {
      shape.phone = Yup.string()
        .required("Phone number is required")
        .test("is-valid-phone", "Phone number is not valid", (value) =>
          isValidPhoneNumber(value || "")
        );
      shape.email = Yup.string().email("Email is invalid").optional();
      shape.otp = Yup.string().required("OTP is required");
    } else {
      shape.email = Yup.string()
        .email("Email is invalid")
        .required("Email is required");
      shape.phone = Yup.string()
        .test("is-valid-phone", "Phone number is not valid", (value) =>
          value ? isValidPhoneNumber(value || "") : true
        )
        .optional();
      shape.otp = Yup.string().required("OTP is required");
    }

    return Yup.object().shape(shape);
  }, [otpEnabled]);

  const [Data, setData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    Phone: "",
    Requests: "",
    isAgree: false,
    otp: "",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  useEffect(() => {
    if (!otpSent || otpVerified) {
      setResendSeconds(0);
      return undefined;
    }
    setResendSeconds(60);
    const interval = setInterval(() => {
      setResendSeconds((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [otpSent, otpVerified]);

  const {
    register,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  const watchedPhone = watch("phone") || Data.Phone || "";
  const watchedEmail = watch("email") || Data.Email || "";
  const watchedOtp = watch("otp") || Data.otp || "";

  const resetVerification = () => {
    setOtpSent(false);
    setOtpVerified(false);
    setResendSeconds(0);
    setValue("otp", "");
    setData((prev) => ({ ...prev, otp: "" }));
    try {
      localStorage.removeItem("booking_auth_token");
    } catch (e) {
      // ignore storage errors
    }
    if (onPhoneVerified) {
      onPhoneVerified(false);
    }
  };

  const handleChange = async (e, t) => {
    setData((prev) => ({ ...prev, [t]: e?.target?.value }));
  };

  const handleChangeCheckbox = async (e, t) => {
    setData((prev) => ({ ...prev, [t]: e?.target?.checked }));
  };

  const handlePhoneChange = (val, onChange) => {
    if (otpEnabled && otpVerified) return;
    const next = val || "";
    const prevPhone = getValues("phone") || "";
    onChange(next);
    setData((prev) => ({ ...prev, Phone: next }));
    // Only clear OTP/verification when the number actually changes (mobile OTP mode)
    if (otpEnabled && next !== prevPhone && (otpSent || getValues("otp"))) {
      resetVerification();
    }
  };

  const handleEmailChange = (val, onChange) => {
    if (!otpEnabled && otpVerified) return;
    const next = val || "";
    const prevEmail = getValues("email") || "";
    onChange(next);
    setData((prev) => ({ ...prev, Email: next }));
    // Only clear OTP/verification when the email actually changes (email OTP mode)
    if (!otpEnabled && next !== prevEmail && (otpSent || getValues("otp"))) {
      resetVerification();
    }
  };

  const handleSendPhoneOtp = async () => {
    const phoneRaw = getValues("phone");
    try {
      const parsed = parsePhoneNumber(phoneRaw || "");
      if (!parsed?.isValid()) throw new Error("Invalid number");

      setOtpLoading(true);
      await postAPI(endpoint, "mobile-otp", {
        mobile_no: parsed.nationalNumber,
        otp_type: otpSent ? "resend" : "send",
        country_code: `+${parsed.countryCallingCode}`,
      });

      showSnackbar({ message: "OTP sent successfully", severity: "success" });
      setOtpLoading(false);
      setOtpSent(true);
    } catch (err) {
      showSnackbar({
        message: err?.message || "Failed to send OTP",
        severity: "error",
      });
      setOtpLoading(false);
    }
  };

  const handleSendEmailOtp = async () => {
    const email = getValues("email");
    try {
      if (!email || !email.includes("@")) {
        throw new Error("Please enter a valid email");
      }

      setOtpLoading(true);
      await postAPI(endpoint, "email-otp", {
        email: email,
        otp_type: otpSent ? "resend" : "send",
      });

      showSnackbar({ message: "OTP sent to your email", severity: "success" });
      setOtpLoading(false);
      setOtpSent(true);
    } catch (err) {
      showSnackbar({
        message: err?.message || "Failed to send OTP",
        severity: "error",
      });
      setOtpLoading(false);
    }
  };
  const handleVerifyPhoneOtp = async () => {
    const phoneRaw = getValues("phone");
    const otp = getValues("otp");
    try {
      const parsed = parsePhoneNumber(phoneRaw || "");
      if (!parsed?.isValid()) throw new Error("Invalid number");

      setVerifyLoading(true);
      const resp = await postAPI(endpoint, "mobile-verify-otp", {
        mobile_no: parsed.nationalNumber,
        country_code: `+${parsed.countryCallingCode}`,
        otp,
        came_from: "Web",
      });

      if (!resp?.success) throw new Error(resp?.message || "That code is incorrect. Please try again.");

      const token =
        resp?.data?.access_token || resp?.data?.token || resp?.access_token;
      if (!token) {
        throw new Error("We couldn’t sign you in. Please request a new code.");
      }

      showSnackbar({ message: "Code verified", severity: "success" });
      setOtpVerified(true);
      localStorage.setItem("booking_auth_token", token);
      if (onPhoneVerified) {
        onPhoneVerified(token);
      }
    } catch (err) {
      showSnackbar({
        message: err?.response?.data?.message || err?.message || "That code is incorrect. Please try again.",
        severity: "error",
      });
    } finally {
      setVerifyLoading(false);
    }
  };
  const handleVerifyEmailOtp = async () => {
    const email = getValues("email");
    const otp = getValues("otp");
    try {
      setVerifyLoading(true);
      const resp = await postAPI(endpoint, "email-otp-verification", {
        email: email,
        code: otp,
        came_from: "Web",
      });

      if (!resp?.success) {
        throw new Error(resp?.message || "That code is incorrect. Please try again.");
      }

      const token =
        resp?.data?.access_token || resp?.data?.token || resp?.access_token;
      if (!token) {
        throw new Error("We couldn’t sign you in. Please request a new code.");
      }

      showSnackbar({ message: "Code verified", severity: "success" });
      setOtpVerified(true);
      localStorage.setItem("booking_auth_token", token);
      if (onPhoneVerified) {
        onPhoneVerified(token);
      }
    } catch (err) {
      showSnackbar({
        message: err?.response?.data?.message || err?.message || "That code is incorrect. Please try again.",
        severity: "error",
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (otpEnabled) {
      await handleSendPhoneOtp();
    } else {
      await handleSendEmailOtp();
    }
  };

  const handleVerifyOtp = async () => {
    if (otpEnabled) {
      await handleVerifyPhoneOtp();
    } else {
      await handleVerifyEmailOtp();
    }
  };

  useEffect(() => {
    handleData(Data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Data]);
  const isSendOtpDisabled = () => {
    if (otpLoading || otpVerified || resendSeconds > 0) return true;
    if (otpEnabled) {
      return !isValidPhoneNumber(watchedPhone);
    } else {
      return !watchedEmail || !watchedEmail.includes("@");
    }
  };

  return (
    <Fragment>
      <Box>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Phone + OTP — same layout as Fast Booking */}
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="primary"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              {otpEnabled 
                ? "Please verify your number with OTP." 
                : "Please verify your email with OTP."}
                </Typography>
                <Grid
                  container
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Grid item xs={12} sm={otpEnabled ? 8 : 12}>
                    <Controller
                      name="phone"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, onBlur, value } }) => (
                        <PhoneNumberInput
                          id="Phone"
                          name="Phone"
                          label={otpEnabled ? "Mobile *" : "Mobile (Optional)"}
                      required={otpEnabled}
                          variant="outlined"
                          autoComplete="tel"
                          value={value}
                          onBlur={onBlur}
                          onChange={(val) => handlePhoneChange(val, onChange)}
                          error={Boolean(errors.phone)}
                      helperText={errors.phone?.message}
                          disabled={otpEnabled && otpVerified}
                        />
                      )}
                    />
                  </Grid>
                  {otpEnabled && (
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ height: { xs: 48, sm: 56 } }}
                      onClick={handleSendOtp}
                      disabled={isSendOtpDisabled()}
                    >
                      {otpLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : otpSent && !otpVerified ? (
                        resendSeconds > 0 ? `Resend in ${resendSeconds}s` : "Resend"
                      ) : (
                        "Send OTP"
                      )}
                    </Button>
                  </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Grid item xs={12} sm={!otpEnabled ? 8 : 12}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      id="Email"
                      name="Email"
                      label={!otpEnabled ? "Email *" : "Email (Optional)"}
                      required={!otpEnabled}
                      fullWidth
                      variant="outlined"
                      autoComplete="email"
                      value={value}
                      onBlur={onBlur}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleEmailChange(val, onChange);
                      }}
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                      disabled={!otpEnabled && otpVerified}
                    />
                  )}
                />
              </Grid>
              {!otpEnabled && (
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ height: { xs: 48, sm: 56 } }}
                    onClick={handleSendOtp}
                    disabled={isSendOtpDisabled()}
                  >
                    {otpLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : otpSent && !otpVerified ? (
                      resendSeconds > 0 ? `Resend in ${resendSeconds}s` : "Resend"
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>

                {otpSent && (
            <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Grid
                      container
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Grid item xs={12} sm={otpVerified ? 12 : 8}>
                        <Controller
                          name="otp"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Enter OTP *"
                              fullWidth
                              variant="outlined"
                              error={!!errors.otp}
                              helperText={errors.otp?.message}
                              disabled={otpVerified}
                              onChange={(e) => {
                                field.onChange(e);
                                handleChange(e, "otp");
                              }}
                            />
                          )}
                        />
                      </Grid>
                      {!otpVerified && (
                        <Grid item xs={12} sm={4}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ height: { xs: 48, sm: 56 } }}
                            onClick={handleVerifyOtp}
                            disabled={verifyLoading || !watchedOtp}
                          >
                            {verifyLoading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              "Verify OTP"
                            )}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                    {otpVerified && (
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ mt: 1 }}
                      >
                        {otpEnabled ? "Mobile number verified" : "Email verified"}
                      </Typography>
                    )}
                  </Box>
            </Grid>
          )}

         
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="firstName"
              name="firstName"
              label="First name"
              fullWidth
              autoComplete="given-name"
              variant="outlined"
              {...register("fname", {
                onChange: (e) => handleChange(e, "FirstName"),
              })}
              error={Boolean(errors.fname)}
              helperText={errors.fname?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="lastName"
              name="lastName"
              label="Last name"
              fullWidth
              autoComplete="family-name"
              variant="outlined"
              {...register("lname", {
                onChange: (e) => handleChange(e, "LastName"),
              })}
            />
          </Grid>

         
          <Grid item xs={12}>
            <TextField
              id="Requests"
              name="Requests"
              label="Special Requests"
              fullWidth
              autoComplete="requests"
              variant="outlined"
              multiline
              minRows={2}
              onChange={(e) => {
                handleChange(e, "Requests");
              }}
            />
          </Grid>

          {/* Terms & Conditions */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <FormControlLabel
              sx={{ alignItems: "flex-start", m: 0 }}
              control={
                <Controller
                  name="isAgree"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={Boolean(field.value)}
                      sx={{ pt: 0 }}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        handleChangeCheckbox(e, "isAgree");
                      }}
                    />
                  )}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  I Agree to <Link href={termsURL}>Terms & Conditions</Link>{" "}
                  And <Link href={privacyURL}>Privacy Policy</Link>
                </Typography>
              }
            />
            {errors.isAgree?.message && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {errors.isAgree.message}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  );
});

export default AddressForm;
